import { flow } from 'lodash-es'
import { partial } from 'lodash-es'
import { sortBy } from 'lodash-es'
import { makeObservable, observable } from 'mobx'

import lessonClassApi from '@/api/lessonClass.api'
import studentApi from '@/api/student.api'
import { MAX_REQUEST_NUMBER, 가상학생목록 } from '@/constant/common'
import {
  Student,
  StudentAppSettingColl,
  StudentColl,
  StudentDetail,
  StudentGroupWithAttendance,
  StudentGroupWithAttendanceColl,
} from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'
import { LessonClassWithStudentId } from '@/dto/LessonClass/LessonClassWithStudentId'
import { StudentGroups } from '@/dto/Student/StudentGroups'
import ExtendedMap from '@/models/ExtendedMap'
import commonRepo from '@/repository/common.repo'
import { emptyStringToNull } from '@/utils/common'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'
import queryClient from './queryClient'

type CacheData = {
  studentAttendanceColl?: StudentGroupWithAttendanceColl
  studentColl?: StudentColl
}

/***
 * @see Student
 ***/
class StudentController implements Controller {
  adapter = {
    // TODO: 나중에 studentId 주는 API 추가되거나 변경되면 바꿔야함
    lessonClassWithStudentId: makeAdapter({
      from(data: res.LessonClassesIncludeAttendance) {
        return new LessonClassWithStudentId(data)
      },
    }),
    student: makeAdapter({
      from(data: res.Student): Student {
        if (data.name in 가상학생목록) {
          if (commonRepo.academy && !commonRepo.academy.가상학생등록여부) {
            commonRepo.academy?.update({ 가상학생등록여부: true })
          }
          return new Student({ ...data, isVirtual: true })
        }
        return new Student(data)
      },
    }),
    studentDetail: makeAdapter({
      from(data: res.StudentDetail) {
        if (data.name in 가상학생목록) {
          return new StudentDetail({ ...data, isVirtual: true })
        }
        return new StudentDetail(data)
      },
      toCreate(data: StudentDetail): req.CreateStudent {
        return {
          name: data.name,
          schoolType: data.schoolGrade.schoolType as res.Student['schoolType'],
          grade: data.schoolGrade.grade,
          schoolName: data.schoolName,
          attendanceCode: data.attendanceCode,
          email: data.email,
          address: data.address,
          phone: data.phone,
          parentPhone: data.parent.phone,
          homePhone: data.homePhone,
          memo: data.memo,
          startDate: data.startDate,
          birthDate: data.birthDate,
          status: commonRepo.isSchoolflat ? 'INACTIVE' : 'ACTIVE',
        }
      },
      toChange(data: StudentDetail): req.ChangeStudent {
        return {
          ...this.toCreate(data),
          status: data.status,
          allowedServiceTypes: data.allowedServiceTypes,
        }
      },
    }),
    // 학생 앱 정답 공개 설정
    studentAppSettings: makeAdapter({
      from({
        studentColl,
        appSettings,
      }: {
        studentColl: StudentColl
        appSettings: res.StudentAppSetting[]
      }) {
        return new StudentAppSettingColl(studentColl, appSettings)
      },
      to(studentAppSettings: StudentAppSettingColl): req.StudentAppSetting[] {
        return Array.from(studentAppSettings.settings, (setting) => ({
          studentId: setting.studentId, //TODO: studentId로 통일 예정
          showSolutionBeforeScoring: setting.채점전_정답공개,
          showSolutionAfterScoring: setting.채점후_정답해설공개,
          showProblemVideoBeforeScoring: setting.채점전_문풀동공개,
          showProblemVideoAfterScoring: setting.채점후_문풀동공개,
          showSolutionToParent: setting.학부모_공개,
          useAiForHintConceptual: setting.개념힌트_사용,
          useAiForSolveTheStuckUp: setting.풀이분석_사용,
          useAiForReviewTheSolution: setting.풀이확인_사용,
        }))
      },
    }),
    gradeAttendance: makeAdapter({
      from(data: res.StudentIncludeAttendance) {
        // FIXME: 밑에 2개 api에서 정렬해주면 정렬로직 삭제
        return new StudentGroupWithAttendance({
          grade: data.grade,
          schoolType: data.schoolType,
          memberList: sortBy(data.memberList, (member) => member.name),
        })
      },
    }),
    // deprecated
    lessonClassesAttendance: makeAdapter({
      from(data: res.LessonClassesIncludeAttendance) {
        return new StudentGroupWithAttendance({
          id: data.id,
          name: data.name,
          memberList: sortBy(data.memberList, ({ name }) => name),
        })
      },
    }),
  }
  cacheData = {
    studentGroupWithAttendanceColl: undefined,
    studentColl: undefined,
  } as CacheData
  fetchMaximumFilterStudents = flow(
    partial(
      this.fetchStudents,
      new ReqParams({ params: { size: MAX_REQUEST_NUMBER, sorts: [['name', 'ASC']] } })
        .toQueryString,
    ),
    async (promise) => {
      const { studentColl } = await promise
      return studentColl
    },
  )
  private studentGroups = new StudentGroups()

  constructor() {
    makeObservable<this, 'studentGroups'>(this, {
      studentGroups: observable,
    })
  }

  async fetchHasStudent() {
    const {
      data: {
        data: { content },
      },
    } = await studentApi.getStudents(
      new ReqParams({
        params: {
          size: 1,
          page: 0,
        },
      }).toQueryString + '&status=ACTIVE,INACTIVE',
    )

    return content.length > 0
  }
  // 활성 학생 수 가져오기
  async fetchActiveStudents() {
    const {
      data: {
        data: { totalElements },
      },
    } = await studentApi.getStudents(
      new ReqParams({
        params: {
          size: 1,
          page: 0,
          status: 'ACTIVE',
        },
      }).toQueryString,
    )

    return totalElements
  }
  async fetchAttendanceCode() {
    const {
      data: { data },
    } = await studentApi.getAttendanceCode()
    return data
  }

  async fetchStudents(pageQueryString: string, filterOptions?: req.StudentFilterOptions) {
    const {
      data: {
        data: { content, last, totalPages, totalElements },
      },
    } = await studentApi.getStudents(
      pageQueryString,
      filterOptions ? emptyStringToNull(filterOptions) : undefined,
    )
    const studentsData = content.map(this.adapter.student.from)

    return { studentColl: new StudentColl(studentsData), last, totalPages, totalElements }
  }

  async fetchUpdateStudents(pageSizeQueryString: string, filterOptions?: req.StudentFilterOptions) {
    const {
      data: {
        data: { content, last },
      },
    } = await studentApi.getStudents(
      pageSizeQueryString,
      filterOptions ? emptyStringToNull(filterOptions) : undefined,
    )
    const studentsData = content.map(this.adapter.student.from)

    return { studentColl: new StudentColl(studentsData), last }
  }

  async fetchMaximumStudents({
    studentStatus = ['ACTIVE'],
  }: {
    studentStatus?: req.StudentStatusParams['status']
  }) {
    const reqParams = new ReqParams({
      params: {
        size: MAX_REQUEST_NUMBER,
        sorts: [['name', 'ASC']],
      },
    })

    const fetchData = await queryClient.fetchQuery({
      queryKey: ['fetchMaximumStudents', studentStatus],
      queryFn: () => this.fetchStudents(reqParams.toQueryString, { status: studentStatus }),
    })

    if (this.studentGroups.studentColl !== fetchData.studentColl) {
      this.studentGroups.setStudentColl(fetchData.studentColl)
    }

    if (!this.studentGroups.studentColl) throw Error('데이터가 없음!')

    return this.studentGroups.studentColl
  }

  async createVirtualStudents(newStudentInfos: StudentDetail[]) {
    const promises = newStudentInfos.map((student) => {
      return studentApi.postVirtualStudent(this.adapter.studentDetail.toCreate(student))
    })
    return await Promise.all(promises).then(() => {
      queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
      queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
    })
  }

  async createStudent(newStudentInfo: StudentDetail) {
    await studentApi.postStudents(this.adapter.studentDetail.toCreate(newStudentInfo))
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async getStudentsXLS() {
    return await studentApi.getStudentsXLS()
  }

  async createStudentsXLS(params: req.StudentStatusParams, xls: req.StudentXLS) {
    const {
      data: { data },
    } = await studentApi.postStudentsXLS(params, xls)
    return data
  }

  async fetchStudentDetailById(id: req.StudentId) {
    const {
      data: { data },
    } = await studentApi.getStudentDetailById(id)
    return this.adapter.studentDetail.from(data)
  }

  async fetchStudentsById(ids: req.StudentId[]) {
    const {
      data: { data },
    } = await studentApi.getStudentsById(ids)

    const studentsData = data.map(this.adapter.student.from)

    return new StudentColl(studentsData)
  }

  async changeStudentById(studentDetail: StudentDetail) {
    await studentApi.putStudentById(
      studentDetail.id,
      this.adapter.studentDetail.toChange(studentDetail),
    )
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async setStatusActive(data: req.StudentPatchFilterOption) {
    await studentApi.patchStudentActive(data)
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async setStatusInactive(data: req.StudentPatchFilterOption) {
    await studentApi.patchStudentInactive(data)
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async deleteStudents(data: req.StudentDeleteFilterOption, isDeleteAll?: boolean) {
    if (isDeleteAll) {
      await studentApi.deleteAllStudents(data)
    } else {
      await studentApi.deleteStudents(data)
    }
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async getAllStudentsListXLS(data: req.StudentFilterOptions & { studentIds?: req.StudentId[] }) {
    const pageParms = new ReqParams({
      params: {
        status: data.status,
        grade: data.grade,
        schoolType: data.schoolType,
        studentIds: data.studentIds,
        name: data.name,
      },
    })
    const queryString = pageParms.toQueryString
    return await studentApi.getAllStudentsListXLS(queryString)
  }

  /* 학생앱 설정 */
  async fetchAppSettings() {
    const {
      data: { data },
    } = await studentApi.getStudentsAppSetting()
    return data
  }

  async changeStudentAppSettings(
    studentAppSettings: StudentAppSettingColl | req.StudentAppSetting[],
  ) {
    const data = Array.isArray(studentAppSettings)
      ? studentAppSettings
      : this.adapter.studentAppSettings.to(studentAppSettings)
    await studentApi.putStudentsAppSetting(data)
  }

  // 학생 앱 정답 공개 설정
  async fetchStudentAppSettings() {
    const [studentColl, appSettings] = await Promise.all([
      studentController.fetchMaximumFilterStudents({ status: ['ACTIVE'] }),
      this.fetchAppSettings(),
    ])

    const studentAppSettings = this.adapter.studentAppSettings.from({
      studentColl,
      appSettings,
    })

    return studentAppSettings
  }

  // 아래 fetchStudentGroupAttendance을 대체함
  async fetchStudentGroups({
    studentStatus = ['ACTIVE'],
  }: {
    studentStatus?: req.StudentStatusParams['status']
  } = {}) {
    // 내부적으로 side effect 적으로 this.studentGroups의 멤버들을 세팅함...
    // 성능적으로 손해를 보지 않으면서 어떻게 더 좋게 할 수 있을까요?
    return await Promise.all([
      this.fetchMaximumStudents({ studentStatus }),
      this.fetchLessonClassWithStudentColl({ studentStatus }),
    ]).then(() => this.studentGroups)
  }

  async fetchStudentGroupsByStudentIdList(studentIdList: Student['id'][]) {
    // 내부적으로 side effect 적으로 this.studentGroups의 멤버들을 세팅함...
    // 성능적으로 손해를 보지 않으면서 어떻게 더 좋게 할 수 있을까요?

    const lessonClassParams = new ReqParams({
      params: {
        page: 0,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    return Promise.all([
      this.fetchStudentsById(studentIdList),
      queryClient.fetchQuery({
        queryKey: ['fetchLessonClassWithStudentColl'],
        queryFn: async () => {
          const {
            data: {
              data: { content },
            },
          } = await lessonClassApi.getLessonClassesAttendance(lessonClassParams.toQueryString)
          const lessonClassesWithStudentId = content.map(this.adapter.lessonClassWithStudentId.from)
          return ExtendedMap.fromArr(lessonClassesWithStudentId, 'id')
        },
      }),
    ]).then(([studentColl, lessonClassColl]) => {
      const studentGroups = new StudentGroups()
      studentGroups.setStudentColl(studentColl)
      studentGroups.setLessonClassColl(lessonClassColl)
      return studentGroups
    })
  }

  /* studentGroup */
  // 수업 - 학년, 반 학생리스트 정보 가져오기
  // TODO: 나중에 없애(제발) 버려야함(studentIdList를 주게 변경되거나 api가 생기면)
  // !deprecated
  fetchStudentGroupAttendance = async () => {
    const promise = Promise.all([
      this.fetchLessonClassesIncludeAttendance(),
      this.fetchGradeIncludeAttendance(),
    ])

    const cacheData = queryClient.getQueryData<
      [StudentGroupWithAttendance[], StudentGroupWithAttendance[]]
    >(['fetchStudentGroupAttendance'])
    const fetchData = await queryClient.fetchQuery({
      queryKey: ['fetchStudentGroupAttendance'],
      queryFn: () => promise,
    })

    if (cacheData !== fetchData) {
      this.cacheData.studentAttendanceColl = new StudentGroupWithAttendanceColl({
        studentByLessonClassArr: fetchData[0],
        studentByGradeArr: fetchData[1],
      })
    }

    if (!this.cacheData.studentAttendanceColl) throw Error('캐시된 데이터가 없음')

    return this.cacheData.studentAttendanceColl
  }

  // 도메인 기반이 아니라서 둘데가 없어서 여기둬요......
  // TODO: 나중에 변경되어야함(studentIdList를 주게 변경되거나 api가 생기면)
  async fetchLessonClassWithStudentColl({
    studentStatus = ['ACTIVE'],
  }: {
    studentStatus?: req.StudentStatusParams['status']
  }) {
    const params = new ReqParams({
      params: {
        page: 0,
        studentStatus,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    // queryClient에 저장된 cachedData가 queryClient로 새로 불러온 fetchData와 reference가 같다면,
    // 전역 데이터인 기존 this.cacheData를 리턴하고(새로운 데이터로 전역 데이터를 교체하는 비용을 들이지 않음),
    // 다르다면 this.cachedData.lessonClassColl에 fetch해온 것을 넣어 전역 데이터를 만든다.
    // 목적 : 새로운 데이터로 전역데이터를 교체하는 비용을 들이지 않는게 포인트
    const lessonClassWithStudentColl = await queryClient.fetchQuery({
      queryKey: ['fetchLessonClassWithStudentColl', studentStatus],
      queryFn: async () => {
        const {
          data: {
            data: { content },
          },
        } = await lessonClassApi.getLessonClassesAttendance(params.toQueryString)
        const lessonClassesWithStudentId = content.map(this.adapter.lessonClassWithStudentId.from)
        return ExtendedMap.fromArr(lessonClassesWithStudentId, 'id')
      },
    })

    if (this.studentGroups.lessonClassColl !== lessonClassWithStudentColl) {
      this.studentGroups.setLessonClassColl(lessonClassWithStudentColl)
    }

    if (!this.studentGroups.lessonClassColl) throw Error('데이터가 없음!')

    return this.studentGroups.lessonClassColl
  }

  // deprecated
  private fetchLessonClassesIncludeAttendance = async () => {
    const params = new ReqParams({
      params: {
        page: 0,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    const queryString = params.toQueryString
    const {
      data: {
        data: { content },
      },
    } = await lessonClassApi.getLessonClassesAttendance(queryString)

    const attendanceArr = content.map(this.adapter.lessonClassesAttendance.from)

    return attendanceArr
  }

  // deprecated

  private fetchGradeIncludeAttendance = async () => {
    const pageParms = new ReqParams({
      params: {
        page: 0,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    const queryString = pageParms.toQueryString
    const {
      data: { data },
    } = await studentApi.getStudentAttendance(queryString)

    const attendanceArr = data.map(this.adapter.gradeAttendance.from)
    return attendanceArr
  }
}

export const studentController = new StudentController()
